import React from 'react';
import { ShieldAlert, AlertCircle, AlertTriangle, Info } from 'lucide-react';

export type SeverityKey = 'critical' | 'high' | 'medium' | 'low';

// Colours come from the design-system severity tokens (theme-aware).
export const SEVERITY_META: Record<
  SeverityKey,
  { label: string; icon: typeof ShieldAlert; bg: string; text: string; border: string; bar: string }
> = {
  critical: {
    label: 'Critical',
    icon: ShieldAlert,
    bg: 'bg-[var(--sev-critical-bg)]',
    text: 'text-[color:var(--sev-critical)]',
    border: 'border-[color:var(--sev-critical-border)]',
    bar: 'bg-[var(--sev-critical)]',
  },
  high: {
    label: 'High',
    icon: AlertCircle,
    bg: 'bg-[var(--sev-high-bg)]',
    text: 'text-[color:var(--sev-high)]',
    border: 'border-[color:var(--sev-high-border)]',
    bar: 'bg-[var(--sev-high)]',
  },
  medium: {
    label: 'Medium',
    icon: AlertTriangle,
    bg: 'bg-[var(--sev-medium-bg)]',
    text: 'text-[color:var(--sev-medium)]',
    border: 'border-[color:var(--sev-medium-border)]',
    bar: 'bg-[var(--sev-medium)]',
  },
  low: {
    label: 'Low',
    icon: Info,
    bg: 'bg-[var(--sev-low-bg)]',
    text: 'text-[color:var(--sev-low)]',
    border: 'border-[color:var(--sev-low-border)]',
    bar: 'bg-[var(--sev-low)]',
  },
};

export const SEVERITY_ORDER: SeverityKey[] = ['critical', 'high', 'medium', 'low'];

// Severity → rank (0 = most severe). Drives "critical first" sorting.
const SEVERITY_RANK: Record<string, number> = SEVERITY_ORDER.reduce(
  (acc, sev, i) => {
    acc[sev] = i;
    return acc;
  },
  {} as Record<string, number>,
);

/**
 * Comparator that orders findings by severity, most critical first
 * (critical → high → medium → low). Unknown severities sort last.
 * Use with `[...findings].sort(compareBySeverity)`.
 */
export const compareBySeverity = (a: { severity: string }, b: { severity: string }): number =>
  (SEVERITY_RANK[a.severity] ?? 99) - (SEVERITY_RANK[b.severity] ?? 99);

/**
 * Compact, colored severity count pills. Renders one pill per non-zero severity,
 * or a single green "empty" pill when the total is zero.
 *
 * When `showLabel` is true (default) each pill shows the icon and severity name
 * (e.g. "2 Critical"). When false, pills show just the count in the colored badge
 * (the severity is conveyed by color, with the name available on hover) — used in
 * space-constrained contexts like cards.
 */
export const SeverityPills: React.FC<{
  summary: Partial<Record<SeverityKey, number>> & { total?: number };
  emptyLabel?: string;
  showLabel?: boolean;
}> = ({ summary, emptyLabel = 'No vulnerabilities', showLabel = true }) => {
  const total = SEVERITY_ORDER.reduce((acc, sev) => acc + (summary[sev] || 0), 0);
  if (total === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border bg-[var(--status-resolved-bg)] text-[color:var(--status-resolved)] border-[color:var(--status-resolved-bg)]">
        {emptyLabel}
      </span>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {SEVERITY_ORDER.map((sev) => {
        const count = summary[sev] || 0;
        if (!count) return null;
        const meta = SEVERITY_META[sev];
        const Icon = meta.icon;
        return (
          <span
            key={sev}
            title={meta.label}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${meta.bg} ${meta.text} ${meta.border}`}
          >
            {showLabel ? (
              <>
                <Icon className="w-3.5 h-3.5" />
                {count} {meta.label}
              </>
            ) : (
              count
            )}
          </span>
        );
      })}
    </div>
  );
};
